import React, { useCallback, useEffect, useState } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { BtnGhost, BtnGold } from './MarketplaceAtoms.js';
import { isComunidadEventoN8n, comunidadFuenteDominio } from '../../lib/comunidadEventosN8n.js';
import { getComunidadEventDate } from '../../lib/comunidadEventDate.js';
import {
  dismissComunidadDuplicateCheck,
  getComunidadDuplicateCheck,
  loadComunidadDuplicateCandidate,
  refreshComunidadDuplicateCheck,
} from '../../lib/comunidadDuplicateCheck.js';

function formatFechaEvento(row) {
  const c = row?.cuerpo && typeof row.cuerpo === 'object' ? row.cuerpo : {};
  const d = getComunidadEventDate({
    _cuerpo: c,
    fuenteFecha: row?.fuente_fecha,
    date: c.fecha,
  });
  if (!d) return c.fecha ? String(c.fecha) : '—';
  try {
    return new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }).format(d);
  } catch {
    return String(c.fecha || '—');
  }
}

function sitioBreve(row) {
  const c = row?.cuerpo && typeof row.cuerpo === 'object' ? row.cuerpo : {};
  const p = [c.ciudad, c.via].filter(Boolean).join(', ');
  return String(p || c.place || '').trim() || '—';
}

function imagenUrl(row) {
  const c = row?.cuerpo && typeof row.cuerpo === 'object' ? row.cuerpo : {};
  const u = String(c.imagenUrl || '').trim();
  return u && /^https?:\/\//i.test(u) ? u : null;
}

function statusBadge(label) {
  const map = {
    feed: { text: 'En el feed', color: C.green },
    borrador_n8n: { text: 'Borrador n8n', color: C.gold },
    borrador_usuario: { text: 'Pendiente usuario', color: '#8b9dff' },
  };
  const m = map[label] || { text: label, color: 'rgba(255,255,255,0.5)' };
  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: 800,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: m.color,
        border: `0.5px solid ${m.color}55`,
        borderRadius: 6,
        padding: '2px 6px',
      }}
    >
      {m.text}
    </span>
  );
}

function PreviewCard({ row, label, highlight }) {
  const img = imagenUrl(row);
  const esN8n = isComunidadEventoN8n(row);
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        borderRadius: 12,
        border: highlight ? `1px solid ${C.gold}66` : '1px solid rgba(255,255,255,0.1)',
        background: 'rgba(255,255,255,0.03)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: '10px 12px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          fontSize: 10,
          fontWeight: 800,
          color: highlight ? C.gold : 'rgba(255,255,255,0.55)',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
      <div style={{ aspectRatio: '16/9', background: '#0a0a0f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {img ? (
          <img
            src={img}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            loading="lazy"
          />
        ) : (
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>Sin imagen</span>
        )}
      </div>
      <div style={{ padding: '12px 14px', flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {row?.aprobada ? statusBadge('feed') : esN8n ? statusBadge('borrador_n8n') : statusBadge('borrador_usuario')}
        </div>
        <h4 style={{ margin: 0, fontSize: 14, fontWeight: 750, lineHeight: 1.35, color: '#fff' }}>{row?.title || '—'}</h4>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.45 }}>
          <div>
            <strong style={{ color: 'rgba(255,255,255,0.45)' }}>Fecha:</strong> {formatFechaEvento(row)}
          </div>
          <div>
            <strong style={{ color: 'rgba(255,255,255,0.45)' }}>Lugar:</strong> {sitioBreve(row)}
          </div>
          {row?.fuente_url ? (
            <div style={{ marginTop: 4 }}>
              <a
                href={row.fuente_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: C.gold, fontSize: 10 }}
              >
                Fuente ({comunidadFuenteDominio(row.fuente_url)}) →
              </a>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Comparación lado a lado: borrador vs candidato duplicado.
 */
export default function AdminComunidadDuplicateCompareModal({
  open,
  sourceRow,
  onClose,
  onDismiss,
  onRefresh,
  supabase,
  onError,
  onSuccess,
}) {
  const [candidateRow, setCandidateRow] = useState(null);
  const [candidateIndex, setCandidateIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const dc = getComunidadDuplicateCheck(sourceRow);
  const candidates = Array.isArray(dc?.candidates) ? dc.candidates : [];
  const activeCandidate = candidates[candidateIndex] ?? null;

  const loadCandidate = useCallback(async () => {
    if (!activeCandidate?.id || !supabase) {
      setCandidateRow(null);
      return;
    }
    setLoading(true);
    try {
      const r = await loadComunidadDuplicateCandidate(supabase, activeCandidate.id);
      if (!r.ok) {
        onError?.(r.error || 'No se pudo cargar el candidato.');
        setCandidateRow(null);
        return;
      }
      setCandidateRow(r.row);
    } finally {
      setLoading(false);
    }
  }, [activeCandidate?.id, supabase, onError]);

  useEffect(() => {
    if (!open) {
      setCandidateRow(null);
      setCandidateIndex(0);
      return;
    }
    loadCandidate();
  }, [open, loadCandidate, candidateIndex]);

  if (!open || !sourceRow) return null;

  const handleRefresh = async () => {
    setBusy(true);
    onError?.('');
    try {
      const r = await refreshComunidadDuplicateCheck(supabase, sourceRow.id);
      if (!r.ok) {
        onError?.(r.error || 'No se pudo recalcular.');
        return;
      }
      onRefresh?.(r.duplicate_check);
      onSuccess?.('Comprobación de duplicados actualizada.');
      setCandidateIndex(0);
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = async () => {
    setBusy(true);
    onError?.('');
    try {
      const r = await dismissComunidadDuplicateCheck(supabase, sourceRow.id);
      if (!r.ok) {
        onError?.(r.error || 'No se pudo descartar.');
        return;
      }
      onDismiss?.(r.duplicate_check);
      onSuccess?.('Marcado como no duplicado.');
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div
        role="presentation"
        onClick={() => !busy && onClose?.()}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', zIndex: 2050 }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Comparar posible duplicado"
        onClick={e => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2060,
          width: 'min(920px, 96vw)',
          maxHeight: 'min(90vh, 800px)',
          overflowY: 'auto',
          background: 'linear-gradient(180deg, #1a1d28 0%, #12141c 100%)',
          borderRadius: 14,
          border: '1px solid rgba(200,168,75,0.28)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.55)',
          padding: '18px 18px 14px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#fff' }}>Comparar posible duplicado</h3>
            <p style={{ margin: '6px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.45)', lineHeight: 1.45 }}>
              Revisa las dos publicaciones. Si no es el mismo evento, descarta el aviso.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 20, cursor: 'pointer' }}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        {activeCandidate ? (
          <div
            style={{
              marginBottom: 12,
              padding: '10px 12px',
              borderRadius: 10,
              background: 'rgba(226,75,74,0.08)',
              border: '0.5px solid rgba(226,75,74,0.25)',
              fontSize: 11,
              color: 'rgba(255,255,255,0.75)',
            }}
          >
            <strong style={{ color: C.gold }}>Coincidencias ({activeCandidate.confidence}%):</strong>{' '}
            {(activeCandidate.match_reasons || []).join(' · ')}
          </div>
        ) : null}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
          <PreviewCard row={sourceRow} label="Esta publicación" highlight />
          {loading ? (
            <div
              style={{
                flex: 1,
                minWidth: 240,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'rgba(255,255,255,0.45)',
                fontSize: 12,
              }}
            >
              Cargando candidato…
            </div>
          ) : candidateRow ? (
            <PreviewCard row={candidateRow} label="Posible duplicado" highlight={false} />
          ) : (
            <div style={{ flex: 1, minWidth: 240, fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
              Sin candidato cargado.
            </div>
          )}
        </div>

        {candidates.length > 1 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
            {candidates.map((c, i) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCandidateIndex(i)}
                style={{
                  padding: '4px 10px',
                  borderRadius: 8,
                  border: `0.5px solid ${i === candidateIndex ? C.gold : 'rgba(255,255,255,0.15)'}`,
                  background: i === candidateIndex ? 'rgba(200,168,75,0.12)' : 'transparent',
                  color: i === candidateIndex ? C.gold : 'rgba(255,255,255,0.55)',
                  fontSize: 10,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Candidato {i + 1} ({c.confidence}%)
              </button>
            ))}
          </div>
        ) : null}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
          <BtnGhost type="button" style={{ fontSize: 11 }} disabled={busy} onClick={handleRefresh}>
            Recalcular
          </BtnGhost>
          <BtnGhost type="button" style={{ fontSize: 11 }} disabled={busy} onClick={onClose}>
            Cerrar
          </BtnGhost>
          <BtnGold type="button" style={{ width: 'auto', minWidth: 160, fontSize: 11 }} disabled={busy} onClick={handleDismiss}>
            No es duplicado
          </BtnGold>
        </div>
      </div>
    </>
  );
}
